import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiX } from "react-icons/fi";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  ModalFormField,
  ModalLabel,
  ModalInput,
} from "../ContractsList/NewContractModalStyles";
import { Button } from "../../../Components/Button";
import { useContracts } from "../../../Common/Hooks/useContracts";
import { Contract } from "../../../Common/Types-DTOs/Contracts";
import { isErr } from "../../../Common/Utils/Result";
import { eventBus } from "../../../Common/EventBus";
import { Notification } from "../../../Components/Notifications";

interface EditContractModalProps {
  isOpen: boolean;
  contract: Contract;
  onClose: () => void;
  onSaved: (contract: Contract) => void;
}

export const EditContractModal = ({ isOpen, contract, onClose, onSaved }: EditContractModalProps) => {
  const { t } = useTranslation();
  const { updateContractMetadata } = useContracts();
  const [title, setTitle] = useState(contract.title);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTitle(contract.title);
  }, [isOpen, contract]);

  const canSubmit = title.trim();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    const result = await updateContractMetadata(contract.id, { title: title.trim() });
    setIsSubmitting(false);

    if (isErr(result)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("contracts.contractDetail.editModal.notifications.updateFailed"))
      );
      return;
    }

    onSaved(result.value.contract);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{t("contracts.contractDetail.editModal.title")}</ModalTitle>
          <ModalCloseButton onClick={onClose}>
            <FiX size={18} />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          <ModalFormField>
            <ModalLabel>{t("contracts.contractDetail.editModal.titleFieldLabel")}</ModalLabel>
            <ModalInput value={title} onChange={(e) => setTitle(e.target.value)} />
          </ModalFormField>
        </ModalBody>

        <ModalFooter>
          <Button onClick={handleSubmit} disabled={!canSubmit} isLoading={isSubmitting}>
            {t("contracts.contractDetail.editModal.saveButton")}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};
